import React from 'react';

import ErrorMessageNewFund from '../../components/ErrorMessages/ErrorMessageNewFund';
import ErrorMessageNoData from '../../components/ErrorMessages/ErrorMessageNoData';
import Card from '../../components/Card/Card';
import ChartTooltip from '../../components/ChartTooltip/ChartTooltip';
import ChartWrapper from '../../components/ChartWrapper/ChartWrapper';
import GqlQueryWrapper from '../../components/GqlQueryWrapper/GqlQueryWrapper';
import LineChart from '../../components/LineChart/LineChart';
import Table from '../../components/Table/Table';
import TablePreRender from '../../components/Table/Table.prerender';
import TitleWithDescription from '../../components/TitleWithDescription/TitleWithDescription';
import Tooltip from '../../components/Tooltip/Tooltip';
import tooltipIcon from '../../images/grey-tooltip.svg';
import ViewDataTable from '../../components/ViewDataTable/ViewDataTable';

import { ETF_DAYS_PREMIUM } from './queries';
import { getDaysAtPremiumMapData } from './util';
import { displayShortDateOrNA } from '../../utils/Format/Format';
import { quarterEndBeforeInception } from '../../utils/fund-info';
import { CARD_NAMES } from '../../utils/constants';

import styles from './DaysAtPremium.css';

const getAxisIntervalByMonth = (data) =>
    new Set(data.map((ele) => ele.label).map((dateLabel) => dateLabel[0])).size;

const DaysAtPremium = (props) => {
    const { ticker } = props;
    const xAxisFormat = ['%b', '%b'];

    return (
        <GqlQueryWrapper
            query={ETF_DAYS_PREMIUM}
            variables={{ ticker }}
            dataFunc={getDaysAtPremiumMapData}
            placeholder={() => <TablePreRender />}
        >
            {({
                data: {
                    shortName,
                    asOfDate,
                    inceptionDate,
                    colors,
                    lineData,
                    combinedTableData,
                    disclaimer
                }
            }) => {
                const dataError =
                    !quarterEndBeforeInception(inceptionDate) &&
                    (!lineData || !combinedTableData);

                return (
                    <React.Fragment>
                        <Card>
                            {quarterEndBeforeInception(inceptionDate) ? (
                                <ErrorMessageNewFund
                                    cardName={CARD_NAMES.DAYS_PREMIUM_DISCOUNT}
                                />
                            ) : (
                                lineData &&
                                combinedTableData && (
                                    <React.Fragment>
                                        <div className="title-group">
                                            <div className="title-section">
                                                <div className="title-container">
                                                    <TitleWithDescription title="Days at Premium/Discount to Nav" />
                                                    <span className="premium-card-tooltip">
                                                        <Tooltip
                                                            id="premium-discount-tip"
                                                            content={
                                                                <div className="etf-tip-content">
                                                                    The
                                                                    relationship
                                                                    between an
                                                                    ETF&apos;s
                                                                    market price
                                                                    and net
                                                                    asset value
                                                                    (NAV). The
                                                                    market price
                                                                    of ETFs are
                                                                    driven in
                                                                    part by the
                                                                    supply of
                                                                    and demand
                                                                    for its
                                                                    shares.
                                                                    Therefore,
                                                                    the market
                                                                    price may be
                                                                    above NAV (a
                                                                    premium) or
                                                                    below NAV (a
                                                                    discount).
                                                                </div>
                                                            }
                                                        >
                                                            <img
                                                                className="tooltip"
                                                                src={
                                                                    tooltipIcon
                                                                }
                                                                height="14px"
                                                                alt="Premium Discount Tooltip"
                                                            />
                                                        </Tooltip>
                                                    </span>
                                                </div>
                                                <span className="as-of-date">
                                                    Quarterly (as of{' '}
                                                    {displayShortDateOrNA(
                                                        asOfDate
                                                    )})
                                                </span>
                                            </div>
                                        </div>
                                        <div className="dividing-line" />
                                        <div className="chart-wrapper">
                                            <ChartWrapper
                                                tipContent={(d) => {
                                                    const formattedData = {
                                                        ...d,
                                                        label: d.headers[0],
                                                        headers: [d.label]
                                                    };
                                                    return (
                                                        <ChartTooltip
                                                            data={formattedData}
                                                        />
                                                    );
                                                }}
                                            >
                                                {({
                                                    marginLeft,
                                                    width,
                                                    toggleTooltip,
                                                    updateTooltip
                                                }) => (
                                                    <LineChart
                                                        colors={colors}
                                                        width={width}
                                                        height={300}
                                                        marginBottom={30}
                                                        marginLeft={marginLeft}
                                                        data={lineData}
                                                        toggleTooltip={
                                                            toggleTooltip
                                                        }
                                                        updateTooltip={
                                                            updateTooltip
                                                        }
                                                        formatXAxis={
                                                            xAxisFormat
                                                        }
                                                        formatYAxis="00"
                                                        xTickInterval={getAxisIntervalByMonth(
                                                            lineData
                                                        )}
                                                    />
                                                )}
                                            </ChartWrapper>
                                            <div className="premium-table-wrapper">
                                                <h2 className="premium-table-title">
                                                    Premium and Discount table
                                                </h2>
                                                <Table
                                                    data={combinedTableData}
                                                    customClassName="premium-discount"
                                                />
                                            </div>
                                            <span className="disclaimer">
                                                {disclaimer}
                                            </span>
                                        </div>
                                    </React.Fragment>
                                )
                            )}
                            {dataError && (
                                <ErrorMessageNoData
                                    cardName={CARD_NAMES.DAYS_PREMIUM_DISCOUNT}
                                />
                            )}
                            <style jsx>{styles}</style>
                        </Card>
                        {!(
                            quarterEndBeforeInception(inceptionDate) ||
                            dataError
                        ) && (
                            <ViewDataTable
                                modalId="premium-discount-view-data"
                                dataDescriptor="Current"
                                audioLabel="View Data for daily prices and discounts in a table format"
                            >
                                <table
                                    className="view-data-table"
                                    title="Days at Premium/Discount to Nav Data Table"
                                >
                                    <caption>
                                        Data for daily prices and discounts
                                    </caption>
                                    <thead>
                                        <tr>
                                            <th>{shortName} Date</th>
                                            <th>Premium / Discount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {lineData.map(
                                            ({ label, data: rowData }) => (
                                                <tr key={label}>
                                                    <td
                                                        style={{
                                                            textAlign: 'right'
                                                        }}
                                                    >
                                                        {label}
                                                    </td>
                                                    <td
                                                        style={{
                                                            textAlign: 'right'
                                                        }}
                                                    >
                                                        {/* check if the first character is a dash and wraps the value 
                                                        in parentheses if true. This garauntees that all screen readers 
                                                        read negative values correctly */}
                                                        {`${rowData[0]}`[0] ===
                                                        '-'
                                                            ? `(${rowData[0]})`
                                                            : rowData[0]}
                                                    </td>
                                                </tr>
                                            )
                                        )}
                                    </tbody>
                                </table>
                            </ViewDataTable>
                        )}
                    </React.Fragment>
                );
            }}
        </GqlQueryWrapper>
    );
};

export default DaysAtPremium;
